import { inject, Injectable, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, CanMatchFn, RouterModule, Routes } from '@angular/router';
import { take } from 'rxjs/operators';
import {
  AuthGuard,
  CustomDataRoleGuard,
  DhhDataDashboardGuard,
  DhhRoleOrDhhStatewideSearchAuthGuard,
  InteractiveAccessAuthGuard,
  NonInteractiveAccessAuthGuard,
  NonPortalUserAuthGuard,
  NonVrAuthGuard,
  PortalAuthGuard,
  VrAuthGuard,
} from './auth/auth.guard';
import { Roles } from './auth/auth.service';
import { NavComponent } from './nav/nav.component';
import { EquityCreateCapGuard, EquityLearnerDocumentGuard } from './shared/guards/equity.guard';
import { FamilyGuard } from './shared/guards/family.guard';
import { FeatureFlagGuard } from './shared/guards/feature-flag-guard';
import { CaseSummary } from './shared/models/case';
import { CaseService } from './shared/services/case/case.service';
import { FeatureFlagService } from './shared/services/feature-flags/feature-flag.service';
import { FeatureFlags } from './shared/services/feature-flags/feature-flags';

const idbOff: CanMatchFn = () => inject(FeatureFlagService).featureOff(FeatureFlags.Idb);

@Injectable({
  providedIn: 'root',
})
class CaseResolver {
  constructor(private caseService: CaseService) {}

  resolve(next: ActivatedRouteSnapshot) {
    const caseId = next.paramMap.get('caseId');
    return new Promise<CaseSummary>((res) => this.caseService.getCaseSummary(caseId).pipe(take(1)).subscribe(res));
  }
}

const routes: Routes = [
  {
    path: 'early-access-referral',
    title: 'Early ACCESS Referral',
    loadChildren: () =>
      import('./child-find/early-access-referral/early-access-referral.module').then((mod) => mod.EarlyAccessReferralModule),
  },
  {
    path: 'state-complaint',
    title: 'State Complaint',
    loadChildren: () => import('./state-complaint-public/state-complaint-public.module').then((mod) => mod.StateComplaintPublicModule),
  },
  {
    path: 'mediation',
    title: 'Mediation',
    loadChildren: () => import('./dispute-resolution-public/mediation/mediation.module').then((mod) => mod.MediationModule),
  },
  {
    path: 'due-process',
    title: 'Due Process',
    loadChildren: () => import('./due-process-public/due-process-public.module').then((mod) => mod.DueProcessPublicModule),
  },
  {
    path: '',
    component: NavComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'My Dashboard',
        loadChildren: () => import('./universal-dashboard/universal-dashboard.module').then((mod) => mod.UniversalDashboardModule),
        canActivate: [FamilyGuard],
      },
      {
        path: 'admin',
        title: 'Admin',
        loadChildren: () => import('./admin/admin.module').then((mod) => mod.AdminModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'child-find',
        title: 'Child Find',
        loadChildren: () => import('./child-find/child-find.module').then((mod) => mod.ChildFindModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'cases/:caseId/evaluation',
        title: 'Evaluation',
        loadChildren: () => import('./evaluation/evaluation.module').then((mod) => mod.EvaluationModule),
      },
      {
        path: 'cases/:caseId/reevaluation',
        title: 'Reevaluation',
        loadChildren: () => import('./reevaluation/reevaluation.module').then((mod) => mod.ReevaluationModule),
      },
      {
        path: 'cases/:caseId/reevaluation-plus',
        title: 'Reevaluation Plus',
        loadChildren: () => import('./reeval-plus/reeval-plus.module').then((mod) => mod.ReevalPlusModule),
      },
      {
        path: 'learner-management',
        title: 'Learner Management',
        loadChildren: () => import('./learner-management/learner-management.module').then((mod) => mod.LearnerManagementModule),
      },
      {
        path: 'cases/:caseId/iep',
        title: 'IEP',
        loadChildren: () => import('./iep/iep.module').then((mod) => mod.IepModule),
      },
      {
        path: 'cases/:caseId/ifsp',
        title: 'IFSP',
        loadChildren: () => import('./ifsp/ifsp.module').then((mod) => mod.IfspModule),
      },
      {
        path: 'cases/:caseId/behavior-discipline',
        title: 'Behavior and Discipline',
        loadChildren: () => import('./behavior-discipline/behavior-discipline.module').then((mod) => mod.BehaviorDisciplineModule),
      },
      {
        path: 'cases/:caseId/pwns',
        title: 'Prior Written Notices',
        loadChildren: () => import('./pwn/pwn.module').then((mod) => mod.PwnModule),
        resolve: {
          case: CaseResolver,
        },
      },
      {
        path: 'sif',
        title: 'Student Information Framework',
        loadChildren: () => import('./sif/sif.module').then((mod) => mod.SifModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'tags',
        title: 'Tags',
        loadChildren: () => import('./tags/tags.module').then((mod) => mod.TagsModule),
      },
      {
        path: 'sticky-notes',
        title: 'Sticky Notes',
        loadChildren: () => import('./sticky-notes/sticky-notes.module').then((mod) => mod.StickyNotesModule),
      },
      {
        path: 'users',
        title: 'Users',
        loadChildren: () => import('./users/users.module').then((mod) => mod.UsersModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'consent-signature',
        title: 'Consent Signature',
        loadChildren: () => import('./consent-signature/consent-signature.module').then((mod) => mod.ConsentSignatureModule),
      },
      {
        path: 'monitoring-progress',
        title: 'Monitoring for Progress',
        loadChildren: () => import('./monitoring-progress/monitoring-progress.module').then((mod) => mod.MonitoringProgressModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'medicaid',
        title: 'Medicaid',
        loadChildren: () => import('./medicaid/medicaid.module').then((mod) => mod.MedicaidModule),
      },
      {
        path: 'canned-report',
        title: 'Reporting',
        loadChildren: () => import('./canned-report/canned-report.module').then((mod) => mod.CannedReportModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'portal',
        title: 'Family Portal',
        loadChildren: () => import('./portal/portal.module').then((mod) => mod.PortalModule),
        canActivate: [PortalAuthGuard, NonVrAuthGuard, NonInteractiveAccessAuthGuard],
      },
      {
        path: 'my-tools',
        title: 'My Tools',
        loadChildren: () => import('./my-tools/my-tools.module').then((mod) => mod.MyToolsModule),
        canActivate: [NonVrAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'surveys',
        title: 'Surveys',
        loadChildren: () => import('./surveys/surveys.module').then((mod) => mod.SurveyModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard],
      },
      {
        path: 'vr',
        title: 'Vocational Rehabilitation Portal',
        loadChildren: () => import('./ivrs/ivrs.module').then((mod) => mod.IvrsModule),
        canActivate: [VrAuthGuard],
        canMatch: [FeatureFlagGuard],
        data: { featureFlag: FeatureFlags.Idb },
      },
      {
        path: 'ivrs',
        title: 'Iowa Vocational Rehabilitation Services',
        loadChildren: () => import('./ivrs/ivrs.module').then((mod) => mod.IvrsModule),
        canActivate: [VrAuthGuard],
        canMatch: [idbOff],
      },
      {
        path: 'dhh',
        title: 'Deaf and Hard of Hearing',
        loadChildren: () => import('./dhh/dhh.module').then((mod) => mod.DhhModule),
        canActivate: [DhhRoleOrDhhStatewideSearchAuthGuard],
      },
      {
        // A root path rather than a child of 'dhh': the DHH menu indicator matches with
        // { exact: false }, so nesting here would light up both the DHH panel and the
        // Deafblind Project panel at once. The module's own routes carry
        // DeafblindProjectAuthGuard + FeatureFlagGuard, same as data-dashboard below,
        // which is likewise a DHH feature routed off the root.
        path: 'deafblind-project',
        loadChildren: () => import('./dhh/components/deafblind-project/deafblind-project.module').then((mod) => mod.DeafblindProjectModule),
      },
      {
        path: 'data-dashboard',
        title: 'Data Dashboard',
        loadChildren: () => import('./my-tools/data-dashboard/data-dashboard.routes').then((mod) => mod.dataDashboardRoutes),
        canActivate: [DhhDataDashboardGuard],
      },
      {
        path: 'interactive-access',
        title: 'Interactive Access',
        loadChildren: () => import('./interactive-access/interactive-access.module').then((mod) => mod.InteractiveAccessModule),
        canActivate: [InteractiveAccessAuthGuard],
      },
      {
        path: 'family-member-validation-dashboard',
        title: 'Family Member Validation',
        loadComponent: () =>
          import('./family-member-validation-dashboard/family-member-validation-dashboard.component').then(
            (m) => m.FamilyMemberValidationDashboardComponent
          ),
      },
      {
        path: 'equity/learners/:learnerId/documents',
        title: 'Learner Documents',
        loadComponent: () =>
          import('./my-tools/equity/equity-review/components/equity-iep-review/components/equity-learner-documents/equity-learner-documents.component').then(
            (m) => m.EquityLearnerDocumentsComponent
          ),
        canActivate: [EquityLearnerDocumentGuard],
      },
      {
        path: 'equity/create-corrective-action-plan/:equityReviewId',
        title: 'Create Corrective Action Plan',
        loadComponent: () =>
          import('./my-tools/equity/components/equity-create-cap/equity-create-cap.component').then((m) => m.EquityCreateCapComponent),
        canActivate: [EquityCreateCapGuard],
      },
      {
        path: 'consents',
        title: 'Consents',
        loadComponent: () =>
          import('./consent/family-consent/update-consent-form/update-consent-form.component').then((m) => m.UpdateConsentFormComponent),
      },
      {
        path: 'tile-administration',
        title: 'Tile Administration',
        loadComponent: () =>
          import('./user-dashboard-tiles/tile-administration/tile-administration.component').then((m) => m.TileAdministrationComponent),
        // Same four roles as AuthService.hasUserDashboardTilesRole; the backend re-checks scope on every call.
        canActivate: [FeatureFlagGuard, CustomDataRoleGuard],
        data: {
          featureFlag: FeatureFlags.UserDashboardTiles,
          roles: [Roles.AeaAdminDashboard, Roles.LeaAdminDashboard, Roles.AeaCaseOwnerDashboard, Roles.LeaCaseOwnerDashboard],
        },
      },
    ],
    canActivate: [AuthGuard],
  },
  {
    path: 'auth',
    title: 'Sign In',
    loadChildren: () => import('./auth/auth.module').then((mod) => mod.AuthModule),
  },
  {
    path: 'signedforms',
    title: 'Signed Forms',
    loadChildren: () => import('./signed-forms/signed-forms.module').then((mod) => mod.SignedFormsModule),
  },
  {
    path: 'documents/:learnerId/:documentId',
    title: 'Document',
    loadComponent: () => import('./shared/components/pdf-viewer/pdf-viewer.component').then((m) => m.PdfViewerComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'documents/generic/docid/:documentId',
    title: 'Document',
    loadComponent: () => import('./shared/components/pdf-viewer/pdf-viewer.component').then((m) => m.PdfViewerComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'logged-out-sso',
    title: 'Signed Out',
    loadComponent: () => import('./shared/components/logged-out-sso/logged-out-sso.component').then((m) => m.LoggedOutSsoComponent),
  },
  {
    path: 'sandbox-unauthorized',
    title: 'Not Authorized',
    loadComponent: () =>
      import('./shared/components/sandbox-unauthorized/sandbox-unauthorized-screen.component').then(
        (m) => m.SandboxUnauthorizedScreenComponent
      ),
  },
  {
    path: 'saml-unauthorized',
    title: 'Not Authorized',
    loadComponent: () =>
      import('./shared/components/saml-unauthorized-screen/saml-unauthorized-screen.component').then(
        (m) => m.SamlUnauthorizedScreenComponent
      ),
  },
  {
    path: 'unauthorized',
    title: 'Not Authorized',
    loadComponent: () =>
      import('./shared/components/unauthorized-screen/unauthorized-screen.component').then((m) => m.UnauthorizedScreenComponent),
  },
  {
    path: 'health-check',
    title: 'Health Check',
    loadComponent: () => import('./shared/components/health-check/health-check.component').then((m) => m.HealthCheckComponent),
  },
  {
    path: 'survey-tool',
    title: 'Survey Tool',
    loadChildren: () => import('./survey-tool/survey-tool.module').then((mod) => mod.SurveyToolModule),
  },
  {
    path: 'interactive-access-lockout',
    title: 'Interactive Access Locked',
    loadChildren: () =>
      import('./interactive-access-lockout/interactive-access-lockout.module').then((mod) => mod.InteractiveAccessLockoutModule),
  },
  {
    path: '**',
    title: 'Page Not Found',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then((mod) => mod.NotFoundComponent),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
